import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";

/**
 * EntitlementsApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/entitlementsApi/entitlementsApi.service.js.
 *
 * All endpoints are v2; the legacy used ApiVersionFactory to rewrite `/v1/`
 * → `/v2/`, hardcoded here.
 *
 * `validateSalesRules` forces `saleChannel: "insideSalesIndividual"` and
 * propagates `tenantId` via the `x-b2c-tenant-id` header (preserved 1:1).
 */
const TEMPLATES_BASE_V2 = "/v2/api/event/templates";
const ENTITLEMENTS_VALIDATE_BASE_V2 = "/v2/api/entitlements/validate";

interface EntitlementsPayload {
  id?: string | number;
  salesRuleSetId?: string | number;
  name?: string;
  entitlementConfig?: unknown[];
  salesRuleConfig?: unknown[];
  ticketRuleConfig?: unknown[];
}

interface ValidateSalesRulesProps {
  tenantId?: string;
  shoppingCartId?: string | number;
}

@Injectable({ providedIn: "root" })
export class EntitlementsApiService {
  private readonly http = inject(HttpClient);

  detail(templateId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${TEMPLATES_BASE_V2}/${templateId}/entitlements`),
    );
  }

  create(
    templateId: number | string,
    props: EntitlementsPayload,
  ): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(
        `${TEMPLATES_BASE_V2}/${templateId}/entitlements`,
        {
          eventTemplateId: templateId,
          entitlementConfig: props.entitlementConfig ?? [],
          salesRuleConfig: props.salesRuleConfig ?? [],
          ticketRuleConfig: props.ticketRuleConfig ?? [],
        },
      ),
    );
  }

  update(
    templateId: number | string,
    props: EntitlementsPayload,
  ): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(
        `${TEMPLATES_BASE_V2}/${templateId}/entitlements`,
        {
          id: props.id,
          eventTemplateId: templateId,
          salesRuleSetId: props.salesRuleSetId,
          name: props.name,
          entitlementConfig: props.entitlementConfig,
          salesRuleConfig: props.salesRuleConfig ?? [],
          ticketRuleConfig: props.ticketRuleConfig ?? [],
        },
      ),
    );
  }

  validateSalesRules(
    props: ValidateSalesRulesProps,
  ): Promise<AjsResponse<unknown>> {
    const headers = new HttpHeaders({
      "x-b2c-tenant-id": props.tenantId ?? "",
    });
    return wrapAjs(
      this.http.post<unknown>(
        ENTITLEMENTS_VALIDATE_BASE_V2,
        { id: props.shoppingCartId },
        { headers },
      ),
    );
  }
}
